import { createRouter, createWebHistory } from "vue-router";
import { authGuard } from "@auth0/auth0-vue";

import LoginView from "./views/LoginView.vue";
import ProjectsView from "./views/ProjectsView.vue";
import ProjectDashboardView from "./views/ProjectDashboardView.vue";
import SiteDiariesView from "./views/SiteDiariesView.vue";
import SiteDiaryDetailView from "./views/SiteDiaryDetailView.vue";
import StaffBriefingsView from "./views/StaffBriefingsView.vue";
import StaffBriefingDetailView from "./views/StaffBriefingDetailView.vue";
import DefectsView from "./views/DefectsView.vue";
import DefectDetailView from "./views/DefectDetailView.vue";
import HandoverView from "./views/HandoverView.vue";
import ObservationsView from "./views/ObservationsView.vue";
import ObservationDetailView from "./views/ObservationDetailView.vue";
import SafetyInsightsView from "./views/SafetyInsightsView.vue";
import LibraryView from "./views/LibraryView.vue";
import WorkPacksView from "./views/WorkPacksView.vue";
import WorkPackDetailView from "./views/WorkPackDetailView.vue";
import ActivityView from "./views/ActivityView.vue";
import NotificationsView from "./views/NotificationsView.vue";
import SettingsView from "./views/SettingsView.vue";
import CheckSheetsView from "./views/CheckSheetsView.vue";
import CheckSheetDetailView from "./views/CheckSheetDetailView.vue";
import QualityDashboardView from "./views/QualityDashboardView.vue";
import WeeklyReportsView from "./views/WeeklyReportsView.vue";
import WeeklyReportDetailView from "./views/WeeklyReportDetailView.vue";
import LocationsView from "./views/LocationsView.vue";
import WorkersView from "./views/WorkersView.vue";
import WorkerDetailView from "./views/WorkerDetailView.vue";
import TimesheetsView from "./views/TimesheetsView.vue";
import TimesheetDetailView from "./views/TimesheetDetailView.vue";
import ComplianceView from "./views/ComplianceView.vue";
import FatigueView from "./views/FatigueView.vue";
import AccommodationsView from "./views/AccommodationsView.vue";
import WorkforceMapView from "./views/WorkforceMapView.vue";

const guard = { beforeEnter: authGuard };

export const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: "/login", name: "login", component: LoginView },
    // Public worker onboarding (tokenised invite link) — no auth guard.
    {
      path: "/onboard",
      name: "onboard",
      component: () => import("./views/OnboardingView.vue"),
    },
    { path: "/", redirect: { name: "projects" } },

    // Projects list — the login landing.
    { path: "/projects", name: "projects", component: ProjectsView, ...guard },

    // Inside a project (URL-nested, project-scoped).
    {
      path: "/projects/:projectId/dashboard",
      name: "project-dashboard",
      component: ProjectDashboardView,
      ...guard,
    },
    {
      path: "/projects/:projectId/progress",
      name: "project-progress",
      component: () => import("./views/ProjectProgressView.vue"),
      ...guard,
    },
    {
      path: "/projects/:projectId/team",
      name: "project-team",
      component: () => import("./views/ProjectTeamView.vue"),
      ...guard,
    },
    {
      path: "/projects/:projectId/work-packs",
      name: "work-packs",
      component: WorkPacksView,
      ...guard,
    },
    {
      path: "/projects/:projectId/work-packs/map",
      name: "work-pack-map",
      component: () => import("./views/WorkPackMapView.vue"),
      ...guard,
    },
    {
      path: "/projects/:projectId/work-packs/:id",
      name: "work-pack-detail",
      component: WorkPackDetailView,
      ...guard,
    },
    {
      path: "/projects/:projectId/site-diaries",
      name: "site-diaries",
      component: SiteDiariesView,
      ...guard,
    },
    {
      path: "/projects/:projectId/site-diaries/:id",
      name: "site-diary-detail",
      component: SiteDiaryDetailView,
      ...guard,
    },
    {
      path: "/projects/:projectId/drawings",
      name: "drawings",
      component: () => import("./views/DrawingsView.vue"),
      ...guard,
    },
    {
      path: "/projects/:projectId/drawings/:id",
      name: "drawing-detail",
      component: () => import("./views/DrawingDetailView.vue"),
      ...guard,
    },
    {
      path: "/projects/:projectId/briefings",
      name: "briefings",
      component: StaffBriefingsView,
      ...guard,
    },
    {
      path: "/projects/:projectId/briefings/:id",
      name: "briefing-detail",
      component: StaffBriefingDetailView,
      ...guard,
    },
    {
      path: "/projects/:projectId/quality-insights",
      name: "quality-insights",
      component: QualityDashboardView,
      ...guard,
    },
    {
      path: "/projects/:projectId/check-sheets",
      name: "check-sheets",
      component: CheckSheetsView,
      ...guard,
    },
    {
      path: "/projects/:projectId/activity",
      name: "activity",
      component: ActivityView,
      ...guard,
    },
    {
      path: "/projects/:projectId/notifications",
      name: "notifications",
      component: NotificationsView,
      ...guard,
    },
    {
      path: "/projects/:projectId/settings",
      name: "settings",
      component: SettingsView,
      ...guard,
    },
    {
      path: "/projects/:projectId/check-sheets/:id",
      name: "check-sheet-detail",
      component: CheckSheetDetailView,
      ...guard,
    },
    {
      path: "/projects/:projectId/issues",
      name: "project-issues",
      component: () => import("./views/IssuesView.vue"),
      ...guard,
    },
    {
      path: "/projects/:projectId/defects",
      name: "defects",
      component: DefectsView,
      ...guard,
    },
    {
      path: "/projects/:projectId/defects/:defectId",
      name: "defect-detail",
      component: DefectDetailView,
      ...guard,
    },
    {
      path: "/projects/:projectId/handover",
      name: "handover",
      component: HandoverView,
      ...guard,
    },
    {
      path: "/projects/:projectId/observations",
      name: "observations",
      component: ObservationsView,
      ...guard,
    },
    {
      path: "/projects/:projectId/observations/:observationId",
      name: "observation-detail",
      component: ObservationDetailView,
      ...guard,
    },
    {
      path: "/projects/:projectId/safety-insights",
      name: "safety-insights",
      component: SafetyInsightsView,
      ...guard,
    },
    {
      path: "/projects/:projectId/documents",
      name: "documents",
      component: LibraryView,
      ...guard,
    },
    {
      path: "/projects/:projectId/weekly-reports",
      name: "weekly-reports",
      component: WeeklyReportsView,
      ...guard,
    },
    {
      path: "/projects/:projectId/weekly-reports/:id",
      name: "weekly-report-detail",
      component: WeeklyReportDetailView,
      ...guard,
    },
    {
      path: "/projects/:projectId/structures",
      name: "structures",
      component: LocationsView,
      ...guard,
    },

    // Cross-project Workforce (top-level, flat).
    { path: "/workers", name: "workers", component: WorkersView, ...guard },
    {
      path: "/workers/:id",
      name: "worker-detail",
      component: WorkerDetailView,
      ...guard,
    },
    { path: "/timesheets", name: "timesheets", component: TimesheetsView, ...guard },
    {
      path: "/timesheets/:id",
      name: "timesheet-detail",
      component: TimesheetDetailView,
      ...guard,
    },
    { path: "/compliance", name: "compliance", component: ComplianceView, ...guard },
    { path: "/workforce-map", name: "workforce-map", component: WorkforceMapView, ...guard },
    { path: "/fatigue", name: "fatigue", component: FatigueView, ...guard },
    { path: "/accommodations", name: "accommodations", component: AccommodationsView, ...guard },

    // App-user administration (logins / roles), distinct from the workforce.
    {
      path: "/users",
      name: "users-admin",
      component: () => import("./views/UsersView.vue"),
      ...guard,
    },
    // Self-service: the signed-in user's own details + password.
    {
      path: "/account",
      name: "account",
      component: () => import("./views/AccountView.vue"),
      ...guard,
    },
    // Help & guides.
    {
      path: "/help",
      name: "help",
      component: () => import("./views/HelpView.vue"),
      ...guard,
    },
  ],
});
